import { useState } from "react";
import { Navigate } from "react-router";
import { Loader2 } from "lucide-react";

import { AuthCard } from "@/components/auth/AuthCard";
import { homeForMember, needsAccessResolution } from "@/navigation/member-routing";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { ACCESS_PENDING_ROUTE, WORKSPACE_UNAVAILABLE_ROUTE } from "@/navigation/routes";

/** Active membership without the learner relationship required by ADR 0015. */
export function WorkspaceUnavailablePage() {
  const { name, logoUrl } = useTenantPresentation();
  const {
    session,
    me,
    membershipStatus,
    isLoading,
    role,
    refreshMembership,
    signOut,
  } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  if (membershipStatus === "anonymous") return <Navigate to="/sign-in" replace />;
  if (needsAccessResolution(membershipStatus)) {
    return <Navigate to={ACCESS_PENDING_ROUTE} replace />;
  }
  if (membershipStatus === "loading" || !session) {
    return (
      <AuthCard
        brandName={name}
        logoUrl={logoUrl}
        eyebrow="Learning workspace"
        heading="Checking your workspace"
        lede="Confirming whether this account has a current course enrollment."
      >
        <div role="status" aria-label="Checking your workspace" aria-busy="true" className="grid gap-4">
          <Skeleton className="h-16" />
          <Skeleton className="h-10 w-4/5" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-[var(--control-height)]" />
            <Skeleton className="h-[var(--control-height)]" />
          </div>
        </div>
      </AuthCard>
    );
  }

  const destination = homeForMember(role, me?.workspaces);
  if (destination !== WORKSPACE_UNAVAILABLE_ROUTE) {
    return <Navigate to={destination} replace />;
  }

  const email = session.user?.email ?? null;
  const busy = isLoading || signingOut;

  return (
    <AuthCard
      brandName={name}
      logoUrl={logoUrl}
      eyebrow="Learning workspace"
      heading="No current course enrollment"
      lede="Your membership is active, but this account does not currently have a learning workspace."
    >
      <div className="grid gap-4">
        {email && (
          <div className="rounded-control bg-well px-4 py-3">
            <div className="eyebrow">
              Signed in as
            </div>
            <div className="mt-1 break-words font-medium text-foreground">{email}</div>
          </div>
        )}
        <p className="text-ui text-muted-foreground">
          My Learning becomes available when the academic office links this account to a learner
          record and adds a current course enrollment. If you expected a course here, contact the
          academic office.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            className="w-full"
            disabled={busy}
            onClick={() => {
              void refreshMembership();
            }}
          >
            {isLoading && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Check again
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setSigningOut(true);
              void signOut().finally(() => setSigningOut(false));
            }}
          >
            {signingOut && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Sign out
          </Button>
        </div>
      </div>
    </AuthCard>
  );
}
